import { useLocation } from 'react-use';

import Section from 'molecules/Section/Section';

import ComponentAccolades from 'components/Accolades/Accolades';
import ComponentAccordion from 'components/Accordion';
import ComponentCardDeck from 'components/CardDeck';
import ComponentComparisonTable from 'components/ComparisonTable/ComparisonTable';
import ComponentConversionPanel from 'components/ConversionPanel/ConversionPanel';
import ComponentFeatureGrid from 'components/FeatureGrid/FeatureGrid';
import ComponentHeading from 'components/Heading/Heading';
import ComponentHero from 'components/Hero/Hero';
import InPageFeatures from 'components/InPageFeatures';
import ComponentNewsletterCallToAction from 'components/NewsletterCTA/NewsletterCTA';
import ComponentSingleInstance from 'components/SingleInstance';
import ComponentStatisticPanel from 'components/StatisticPanel/StatisticPanel';
import ComponentSwitchback from 'components/Switchback/Switchback';
import ComponentTabbedSwitcher from 'components/TabbedSwitcher/TabbedSwitcher';
import ComponentTestimonialSlider from 'components/TestimonialSlider/TestimonialSlider';
import ComponentTimeline from 'components/Timeline/Timeline';
import ComponentTrustBar from 'components/TrustBar/TrustBar';
import ComponentVideo from 'components/Video/Video';

import type { ContentfulLayoutSection } from 'graphqlTypes';

type LayoutSectionProps = ContentfulLayoutSection;

export const getBackgroundIsDark = (backgroundColor: LayoutSectionProps['backgroundColor']) => {
  const bgColorIsDark = Number(backgroundColor?.slice(-3)) > 500;

  return bgColorIsDark;
};

const LayoutSection = ({
  id,
  component,
  backgroundColor,
  pageType,
  displayBreadcrumbs,
  ...props
}: LayoutSectionProps) => {
  const backgroundIsDark = getBackgroundIsDark(backgroundColor);
  const { pathname } = useLocation();
  const isNoNav = pathname?.includes('merchantportal');

  const componentGenerator = (validComponent: ContentfulLayoutSection['component']) => {
    switch (validComponent?.__typename) {
      case 'ContentfulComponentHeading':
        return <ComponentHeading {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulComponentConversionPanel':
        return (
          <ComponentConversionPanel {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentTrustbar':
        return <ComponentTrustBar {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulComponentHero':
        return <ComponentHero {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulComponentCardDeck':
        return (
          <ComponentCardDeck
            {...validComponent}
            backgroundColor={backgroundColor}
            backgroundIsDark={backgroundIsDark}
            key={validComponent?.id}
          />
        );
      case 'ContentfulComponentTabbedSwitcher':
        return (
          <ComponentTabbedSwitcher {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentVideo':
        return (
          <ComponentVideo
            {...validComponent}
            backgroundColor={backgroundColor}
            backgroundIsDark={backgroundIsDark}
            key={validComponent?.id}
          />
        );
      case 'ContentfulComponentTestimonialSlider':
        return (
          <ComponentTestimonialSlider
            {...validComponent}
            backgroundIsDark={backgroundIsDark}
            key={validComponent?.id}
          />
        );
      case 'ContentfulComponentFeatureGrid':
        return (
          <ComponentFeatureGrid {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentSwitchback':
        return (
          <ComponentSwitchback
            {...validComponent}
            backgroundIsDark={backgroundIsDark}
            backgroundColor={backgroundColor}
            key={validComponent?.id}
          />
        );
      case 'ContentfulComponentStatisticPanel':
        return (
          <ComponentStatisticPanel {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentSingleInstance':
        return (
          <ComponentSingleInstance {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentComparisonTable':
        return (
          <ComponentComparisonTable {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />
        );
      case 'ContentfulComponentAccordion':
        return <ComponentAccordion {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulComponentAccolades':
        return <ComponentAccolades {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulComponentNewsletterCallToAction':
        return (
          <ComponentNewsletterCallToAction
            {...validComponent}
            backgroundIsDark={backgroundIsDark}
            key={validComponent?.id}
          />
        );
      case 'ContentfulComponentTimeline':
        return <ComponentTimeline {...validComponent} backgroundIsDark={backgroundIsDark} key={validComponent?.id} />;
      case 'ContentfulInPageFeatures':
        return (
          <InPageFeatures
            {...validComponent}
            key={validComponent?.id}
            backgroundIsDark={backgroundIsDark}
            backgroundColor={backgroundColor}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Section id={id} backgroundColor={backgroundColor} isNoNav={isNoNav} {...props}>
      {component && componentGenerator({ ...component, pageType, displayBreadcrumbs })}
    </Section>
  );
};

export default LayoutSection;
